/** The SVG presentation attributes that can reference an element by `url(…#id)`. */
const REFERENCING_ATTRIBUTES = ['filter', 'clip-path', 'mask', 'fill', 'stroke'];

const SELECTOR = REFERENCING_ATTRIBUTES.map(name => `svg [${name}*="://"]`).join(', ');

/**
 * A `url(…)`, quoted — where the target may hold parentheses — or bare: group 1 is the quote,
 * group 2 the quoted target, group 3 the bare one.
 */
const URL_FUNCTION = /url\((?:(["'])(.*?)\1|([^)]*))\)/g;

/**
 * Points every absolute SVG `url(<page>#<id>)` reference under `root` at `currentPage`.
 *
 * In Safari, amCharts writes its filter, clip-path and gradient references as the page's full URL
 * rather than a bare `url(#id)` — its workaround for WebKit bug 189499, where a `<base href>` (this
 * app has one) makes the bare form resolve against the base instead of the page. That URL is taken
 * when the reference is written, so a later `pushState` — the dashboard's day click, a selection
 * change in a tab's matrix params — leaves it naming the previous URL. Safari then treats it as
 * another document and stops painting what it references: gradient columns turn black, a tooltip
 * loses its shadowed background and pointer. Other browsers get bare references from amCharts, so
 * nothing matches.
 */
export function rebaseSvgUrlReferences(currentPage = pageUrl(), root: ParentNode = document): void {
  for (const element of Array.from(root.querySelectorAll(SELECTOR))) {
    for (const name of REFERENCING_ATTRIBUTES) {
      const value = element.getAttribute(name);
      if (!value?.includes('://')) {
        continue;
      }
      const rebased = value.replace(URL_FUNCTION, (match, quote: string | undefined, quoted: string | undefined, bare: string | undefined) => {
        const target = quoted ?? bare ?? '';
        const hash = target.indexOf('#');
        const page = target.slice(0, hash);
        return hash < 0 || !page.includes('://') || page === currentPage
          ? match
          : `url(${quote ?? ''}${currentPage}${target.slice(hash)}${quote ?? ''})`;
      });
      if (rebased !== value) {
        element.setAttribute(name, rebased);
      }
    }
  }
}

/** The current page's URL without its fragment — what amCharts prefixes its references with. */
function pageUrl(): string {
  return location.href.split('#')[0];
}
